import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { trackEvent as trackMixpanelEvent } from "./mixpanel";

// Read measurement ID dynamically from environment variable
export const GA_MEASUREMENT_ID = (process.env.REACT_APP_GA_MEASUREMENT_ID || "").trim();

/**
 * Dynamically loads and initializes Google Analytics using the .env ID
 */
export const initGA = () => {
  if (typeof window === "undefined") return false;
  if (!GA_MEASUREMENT_ID || GA_MEASUREMENT_ID === "disabled") {
    return false;
  }

  // Prevent duplicate script insertion
  if (document.getElementById("ga-gtag-script")) {
    return true;
  }

  // 1. Create and inject the external gtag.js script
  const script = document.createElement("script");
  script.id = "ga-gtag-script";
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // 2. Initialize dataLayer and window.gtag
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    window.dataLayer.push(arguments);
  };

  window.gtag("js", new Date());
  window.gtag("config", GA_MEASUREMENT_ID, {
    send_page_view: false, // Handled dynamically via React Router in PageTracker
  });

  return true;
};

/**
 * Tracks a page view in Google Analytics
 * @param {string} [path] - URL path (e.g., "/sign-in", "/employees")
 * @param {string} [title] - Page title
 */
export const trackPageView = (path, title) => {
  if (typeof window === "undefined" || !GA_MEASUREMENT_ID || GA_MEASUREMENT_ID === "disabled") {
    return;
  }

  if (typeof window.gtag === "function") {
    window.gtag("config", GA_MEASUREMENT_ID, {
      page_path: path || window.location.pathname + window.location.search,
      page_location: window.location.href,
      page_title: title || document.title,
    });
  }
};

/**
 * Tracks custom events in Google Analytics
 * @param {string} action - Event name (e.g., 'login', 'share_post', 'signup')
 * @param {object} [params] - Additional parameters
 */
export const trackEvent = (action, params = {}) => {
  if (typeof window === "undefined" || !GA_MEASUREMENT_ID || GA_MEASUREMENT_ID === "disabled") {
    return;
  }

  if (typeof window.gtag === "function") {
    window.gtag("event", action, params);
  }
};

/**
 * React Router listener component:
 * - Initializes GA dynamically from .env on load
 * - Automatically tracks page views on every route transition
 */
export const PageTracker = () => {
  const location = useLocation();

  useEffect(() => {
    initGA();
  }, []);

  useEffect(() => {
    trackPageView(location.pathname + location.search);
    trackMixpanelEvent("Page Viewed", { path: location.pathname });
  }, [location]);

  return null;
};

const analytics = {
  GA_MEASUREMENT_ID,
  initGA,
  trackPageView,
  trackEvent,
  PageTracker,
};

export default analytics;
